import React from "react";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  FileProtectOutlined,
  FileTextOutlined,
  SafetyCertificateOutlined,
  ShareAltOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import { Button, Card, Grid, Skeleton, Space, Steps, message } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getComplianceCase, validateComplianceCase } from "../api";
import type { ComplianceCaseDetail, DeforestationAssessment } from "./types";
import { DocumentUploadStep } from "./components/DocumentUploadStep";
import { ValidationPanel } from "./components/ValidationPanel";
import { ResolutionPanel } from "./components/ResolutionPanel";
import { PassportPanel } from "./components/PassportPanel";
import { SharePanel } from "./components/SharePanel";
import { AuditTrailPanel } from "./components/AuditTrailPanel";
import { CaseReadinessChecklist } from "./components/CaseReadinessChecklist";
import { EudrTermHint } from "./components/EudrTermHint";
import { documentsMinReady } from "./complianceLabels";
import { ingestAuditNotifications, pushNotification } from "./ux/notificationStore";
import { useNarrowViewport } from "./ux/useNarrowViewport";

export function CaseDetailPage() {
  const { t } = useTranslation();
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const isNarrow = useNarrowViewport("md");
  const [caseData, setCaseData] = React.useState<ComplianceCaseDetail | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [validating, setValidating] = React.useState(false);
  const [step, setStep] = React.useState(0);

  const reload = React.useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    try {
      const data = await getComplianceCase(caseId);
      setCaseData(data);
      if (data.audit_events?.length) {
        ingestAuditNotifications(data.audit_events, caseId, (action) =>
          t(`compliance.notifications.actions.${action}`, { defaultValue: action })
        );
      }
    } catch {
      message.error(t("compliance.caseDetail.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [caseId, t]);

  React.useEffect(() => {
    reload();
  }, [reload]);

  if (!caseId) return null;

  const hasBlockers = caseData?.latest_validation && !caseData.latest_validation.passed;
  const validationPassed = caseData?.latest_validation?.passed === true;
  const lastStep = 4;

  if (loading && !caseData) {
    return (
      <Card>
        <Skeleton active paragraph={{ rows: 8 }} data-testid="case-detail-skeleton" />
      </Card>
    );
  }

  return (
    <Card
      title={
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate("/dds")}>
          {t("compliance.caseDetail.backToCases")}
        </Button>
      }
    >
      {caseData && (
        <>
          <h2>
            <Space size="small" wrap>
              <span>
                {caseData.supplier_name} — {caseData.contract_reference || caseData.id.slice(0, 8)}
              </span>
              <EudrTermHint term="dds" />
            </Space>
          </h2>
          <p style={{ marginTop: 0, color: "rgba(0,0,0,0.65)" }}>
            {t("compliance.case.authorPartyRole.summary")}:{" "}
            {t(`compliance.case.authorPartyRole.${caseData.author_party_role}`)}
            {caseData.client_org_name
              ? ` · ${t("compliance.newCase.actingFor")}: ${caseData.client_org_name}`
              : ""}
          </p>

          <CaseReadinessChecklist caseData={caseData} />

          {(() => {
            const passportReady =
              Boolean(caseData.passport) ||
              caseData.status === "passport_ready" ||
              caseData.status === "shared";
            const pulseClass = passportReady ? " dds-step--pulse" : "";
            return (
              <div className="dds-sticky-steps" data-testid="dds-sticky-steps">
                <Steps
                  current={step}
                  direction={screens.md ? "horizontal" : "vertical"}
                  size={screens.md ? "default" : "small"}
                  onChange={(next) => {
                    if (next >= 3 && hasBlockers) {
                      message.warning(t("compliance.caseDetail.gatePassportHint"));
                      return;
                    }
                    setStep(next);
                  }}
                  style={{ marginBottom: 0 }}
                  items={[
                    {
                      title: t("compliance.caseDetail.steps.documents"),
                      icon: <FileTextOutlined />,
                    },
                    {
                      title: t("compliance.caseDetail.steps.validate"),
                      icon: <SafetyCertificateOutlined />,
                    },
                    {
                      title: t("compliance.caseDetail.steps.resolve"),
                      icon: <ToolOutlined />,
                    },
                    {
                      title: t("compliance.caseDetail.steps.passport"),
                      icon: <FileProtectOutlined className={pulseClass.trim() || undefined} />,
                      disabled: Boolean(hasBlockers),
                      className: pulseClass.trim() || undefined,
                    },
                    {
                      title: t("compliance.caseDetail.steps.share"),
                      icon: passportReady ? (
                        <CheckCircleOutlined className="dds-step--pulse" />
                      ) : (
                        <ShareAltOutlined />
                      ),
                      disabled: Boolean(hasBlockers) || !caseData.passport,
                      className: pulseClass.trim() || undefined,
                    },
                  ]}
                />
              </div>
            );
          })()}

          {step === 0 && <DocumentUploadStep caseData={caseData} onUpdated={reload} />}

          {step === 1 && (
            <>
              <Button
                type="primary"
                loading={validating}
                onClick={async () => {
                  setValidating(true);
                  try {
                    await validateComplianceCase(caseId);
                    message.success(t("compliance.caseDetail.validationComplete"));
                    pushNotification({
                      kind: "validation_done",
                      title: t("compliance.notifications.validationDone"),
                      caseId,
                    });
                    await reload();
                  } catch {
                    message.error(t("compliance.caseDetail.validationFailed"));
                  } finally {
                    setValidating(false);
                  }
                }}
                style={{ marginBottom: 16 }}
              >
                {t("compliance.caseDetail.runValidation")}
              </Button>
              <ValidationPanel
                validation={caseData.latest_validation}
                loading={validating}
                deforestation={
                  (caseData.validation_summary as { deforestation?: DeforestationAssessment } | null)
                    ?.deforestation ?? null
                }
              />
            </>
          )}

          {step === 2 && (
            <ResolutionPanel
              caseData={caseData}
              onUpdated={reload}
              validating={validating}
              setValidating={setValidating}
            />
          )}

          {step === 3 && <PassportPanel caseData={caseData} onUpdated={reload} />}

          {step === 4 && <SharePanel caseData={caseData} onUpdated={reload} />}

          <Card size="small" title={t("compliance.audit.title")} style={{ marginTop: 24 }}>
            <AuditTrailPanel events={caseData.audit_events || []} />
          </Card>

          <div
            className={isNarrow ? "dds-sticky-actions" : undefined}
            data-testid="dds-step-actions"
            data-narrow={isNarrow ? "true" : "false"}
          >
            <Space style={{ width: "100%", justifyContent: "center" }} wrap size="middle">
              <Button
                className="dds-touch-target"
                disabled={step === 0}
                onClick={() => setStep((s) => s - 1)}
              >
                {t("common.back")}
              </Button>
              {step < lastStep && (
                <Button
                  type="primary"
                  className="dds-touch-target"
                  disabled={
                    (step === 0 && !documentsMinReady(caseData.documents)) ||
                    (step === 2 && hasBlockers === true) ||
                    (step === 3 && (!caseData.passport || hasBlockers === true))
                  }
                  onClick={() => {
                    if (step === 2 && hasBlockers) {
                      message.warning(t("compliance.caseDetail.gatePassportHint"));
                      return;
                    }
                    setStep((s) => s + 1);
                  }}
                >
                  {t("common.next")}
                </Button>
              )}
            </Space>
          </div>

          {hasBlockers && step < 3 && (
            <p style={{ marginTop: 16, color: "#cf1322" }}>
              {t("compliance.caseDetail.blockersHint")}{" "}
              {step !== 1 && (
                <Button type="link" size="small" onClick={() => setStep(1)}>
                  {t("compliance.caseDetail.runValidation")}
                </Button>
              )}
            </p>
          )}
          {step === 3 && !validationPassed && (
            <p style={{ marginTop: 8 }}>
              <Button type="primary" ghost onClick={() => setStep(1)}>
                {t("compliance.caseDetail.revalidateCta")}
              </Button>
            </p>
          )}
        </>
      )}
    </Card>
  );
}
